import { V2CopyButton, V2Tooltip } from "@stacklet/ui";

interface BaseProps {
  /** Text-like value rendered in the cell. */
  value: string | number | null | undefined;
  /** Optional hover tooltip for the rendered text. */
  tooltipTitle?: string | null;
}

interface WithCopyButtonProps extends BaseProps {
  /** Renders a right-aligned copy action for the value. */
  copyButton: true;
  /** Semantic noun used in copy tooltip text, e.g. "account id". */
  copyLabel: string;
  /** Optional explicit copy value when it should differ from rendered text. */
  copyValue?: string | number | null | undefined;
}

interface WithoutCopyButtonProps extends BaseProps {
  copyButton?: false;
  copyLabel?: never;
  copyValue?: never;
}

type Props = WithCopyButtonProps | WithoutCopyButtonProps;

/**
 * Renders a single-line truncated value, optionally with a right-aligned copy
 * action.
 *
 * The text is ellipsized within available cell width. If provided,
 * `tooltipTitle` is shown on hover. Set `copyButton` to render a
 * `V2CopyButton`; `copyLabel` and `copyValue` are only valid in that mode.
 */
export function TruncatedText(props: Props) {
  const { value, tooltipTitle } = props;
  const text = value == null ? "" : String(value);
  const display = <span className="block truncate">{text}</span>;
  const textAreaClassName = "min-w-0 w-0 flex-1 overflow-hidden text-left";
  const textContent = tooltipTitle ? (
    <V2Tooltip
      buttonProps={{
        className: textAreaClassName,
      }}
      className="break-all"
      title={tooltipTitle}
    >
      {display}
    </V2Tooltip>
  ) : (
    <span className={textAreaClassName}>{display}</span>
  );

  if (!props.copyButton) {
    return (
      <div className="flex w-full min-w-0 items-center">{textContent}</div>
    );
  }

  return (
    <V2CopyButton
      aria-label={`Copy ${props.copyLabel}`}
      className="!w-full min-w-0 items-center"
      copyLabel={props.copyLabel}
      tooltipTarget="icon"
      value={props.copyValue == null ? text : String(props.copyValue)}
    >
      {textContent}
    </V2CopyButton>
  );
}
